import React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchMembers, UserDetailResponse, saveMember } from "../api/endpoints/users";
import { useAuth } from "../contexts/AuthContext";
import CustomDropdown from "./CustomDropdown";

interface SelectMemberProps {
  className?: string;
  userId: string;
  isAdmin?: boolean;
  htmlId?: string; // template html/file id to assign
  informativeLabel?: string; // label for the dropdown (default: "Assign to User")
  selectUserLabel?: string; // placeholder text for the dropdown (default: "Select user")
  disabled?: boolean; // disable the component when member is already assigned
  onMemberAssigned?: (memberId: string) => void; // callback when member is successfully assigned
}

export default function SelectMember({ className, htmlId, userId, isAdmin, informativeLabel = "Select Member", selectUserLabel = "Select member", disabled = false, onMemberAssigned }: SelectMemberProps) {
  const { user } = useAuth();
  const { data: users, isLoading: usersLoading, isError: usersError } = useQuery<UserDetailResponse[]>({
    queryKey: ["memberlist"],
    queryFn: fetchMembers,
  });

  const [selectedMemberId, setSelectedMemberId] = React.useState<string>("");
  const [status, setStatus] = React.useState<null | { type: "success" | "error"; message: string }>(null);
  const statusTimerRef = React.useRef<number | null>(null);

  const clearStatusLater = React.useCallback(() => {
    if (statusTimerRef.current) window.clearTimeout(statusTimerRef.current);
    statusTimerRef.current = window.setTimeout(() => setStatus(null), 3000);
  }, []);

  React.useEffect(() => () => {
    if (statusTimerRef.current) window.clearTimeout(statusTimerRef.current);
  }, []);

  // When not admin and users fetched successfully, show the current user in dropdown
  React.useEffect(() => {
    if (!isAdmin && userId && users && users.length > 0) {
      setSelectedMemberId(userId);
    }
  }, [isAdmin, userId, users]);

  const { mutate: saveMemberMutate, isPending: isAssigning } = useMutation({
    mutationFn: async ({ emailId, memberId, htmlId: id }: { emailId: string; memberId: string; htmlId: string }) => {
      const res = await saveMember({ emailId, memberId, htmlId: id });
      return res as { success?: boolean; message?: string };
    },
    onSuccess: (data) => {
      const success = !!(data && (data as { success?: boolean }).success);
      const message = (data && (data as { message?: string }).message) || (success ? "Member saved successfully" : "Failed to save member");
      if (success) {
        setStatus({ type: "success", message });
        // Reset dropdown to default state after success
        setSelectedMemberId("");
        // Call parent callback to update template data internally
        setTimeout(() => {
          if (onMemberAssigned) onMemberAssigned(selectedMemberId);
        }, 1000);
      } else {
        setStatus({ type: "error", message });
      }
      clearStatusLater();
    },
    onError: (err: unknown) => {
      const msg = typeof err === "object" && err && (err as { message?: string }).message ? (err as { message: string }).message : "Failed to assign";
      setStatus({ type: "error", message: msg });
      clearStatusLater();
    },
  });

  return (
    <div className={className}>
      <label className="text-xs text-gray-600">{disabled ? "Member already selected" : ''}</label>
      {usersLoading ? (
        <div className="mt-2 flex items-center gap-2">
          <div className="h-8 w-72 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
        </div>
      ) : disabled ? (
        <div className="mt-2 flex items-center gap-2">
          <CustomDropdown
            options={(users ?? []).map((u) => ({ value: u.id, label: u.firstName ?? "", hint: u.emailId ?? "" }))}
            value={userId}
            onChange={() => { }}
            placeholder={selectUserLabel}
            loading={false}
            searchMinChars={3}
            disabled={true}
            className="flex-auto"
            buttonClass="w-full"
          />
          <button
            disabled={true}
            className="text-xs px-3 py-1.5 btn-primary disabled:bg-gray-300 disabled:text-gray-500"
          >
            Selected
          </button>
        </div>
      ) : usersError ? (
        <div className="mt-2 text-xs text-red-500">Failed to load members</div>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <CustomDropdown
            options={(users ?? []).map((u) => ({ value: u.id, label: u.firstName ?? "", hint: u.emailId ?? "" }))}
            value={selectedMemberId}
            onChange={setSelectedMemberId}
            placeholder={selectUserLabel}
            loading={usersLoading}
            searchMinChars={3}
            className="flex-auto"
            buttonClass="w-full"
          />
          <button
            onClick={() => {
              if (!selectedMemberId || !htmlId) return;
              const userEmail = user?.email || "";
              const memberId = selectedMemberId; // use selected user id as member id
              if (!userEmail || !memberId) {
                setStatus({ type: "error", message: "User email or member not found" });
                clearStatusLater();
                return;
              }
              saveMemberMutate({ emailId: userEmail, memberId, htmlId });
            }}
            disabled={!selectedMemberId || !htmlId || isAssigning}
            className="!h-8 min-w-28 text-xs px-3 py-1.5 btn-primary disabled:bg-gray-300 disabled:text-gray-500"
          >
            {isAssigning ? "Saving..." : "Save"}
          </button>
        </div>
      )}
      {status && (
        <div className="mt-2 text-xs">
          {status.type === "success" ? (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-700 border border-green-200">
              ✓ {status.message}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-100 text-red-700 border border-red-200">
              ⚠ {status.message}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
